import { ChangeEvent, useEffect, useReducer, useState } from "react";
import { Header, CustomTable, WarningDialog } from "../../../components";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid2,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { getStaticTexts, PERMISSIONS } from "../../../constants";
import { ResolvedItemStates } from "../../../interfaces";
import { useSelector } from "react-redux";
import {
  getResolvedItems,
  resolveItem,
  updateItemStatus,
} from "../../../services/aggregation";
import { enqueueSnackbar } from "notistack";
import { handleCharacterLimit } from "../../../services/common";
import Loader from "../../../components/Loader";
import { Manage } from "./manage";
import { withPermission } from "../../../components/hoc";
import { getChatListing } from "../../../services/chats";
import ChatDetails from "../../../components/ChatDetails";
import { formatDate } from "../../../services/utils";

const ResolvedItems = () => {
  const language = useSelector((state: any) => state.profile.defaultLanguage);
  const staticTexts = getStaticTexts(language);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout>();
  const categories = useSelector((state: any) => state.category);
  const [showChatListing, setShowChatListing] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const resolvedItemReducer = (state: ResolvedItemStates, action: any) => {
    switch (action.type) {
      case "LOADING":
        return { ...state, loading: true };
      case "LOADED":
        return { ...state, loading: false, list: action.list };
      case "UPDATE_FIELDS":
        return { ...state, ...action.payload };
      case "UPDATE_STATUS":
        return { ...state, loading: true };
      default:
        throw new Error(`Unhandled action type: ${action.type}`);
    }
  };

  const [state, dispatch] = useReducer(resolvedItemReducer, {
    _item: "",
    list: [],
    selectAll: [],
    loading: true,
    searchQuery: "",
    page: 1,
    statusFilter: "",
    categoryFilter: "",
    totalPages: 1,
    manage: {
      isOpen: false,
      type: "view",
      item: null,
    },
    resolveWarning: false,
    claimToUserId: "",
  });

  const handleManage = (type: "new" | "edit" | "view", item: any = null) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        manage: {
          ...state.manage,
          type,
          isOpen: !state.manage.isOpen,
          item,
        },
        claimToUserId: item?._user?._id,
      },
    });
  };

  const handleResolve = async (itemId: string) => {
    try {
      await resolveItem(itemId);
      enqueueSnackbar(staticTexts.itemResolvedSuccessfully, {
        variant: "success",
      });
      window.location.reload();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  const handleWarningDialog = (_item: string) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        resolveWarning: !state.resolveWarning,
        _item,
      },
    });
  };

  const handleClose = () => {
    setShowChatListing(!showChatListing);
  };

  const fetchResolvedItems = async (
    searchQuery: string = "",
    page: number = 1
  ) => {
    try {
      dispatch({ type: "LOADING" });
      const params: any = {
        page,
        limit: itemsPerPage,
        text: searchQuery,
        status: "RESOLVED",
      };

      if (state.statusFilter !== "ALL") {
        params.status = state.statusFilter;
      }

      if (state.categoryFilter !== "ALL") {
        params.categoryId = state.categoryFilter;
      }

      const resolvedItems = (await getResolvedItems(params)) as any;

      if (!resolvedItems || !resolvedItems.items?.length) {
        dispatch({ type: "LOADED", list: [] });
        return;
      }

      dispatch({
        type: "UPDATE_FIELDS",
        payload: { totalPages: resolvedItems.meta.totalPages },
      });

      const list = resolvedItems.items.map((item: any, index: number) => {
        return createRow(item, index, handleManage, handleWarningDialog, page);
      });
      dispatch({ type: "LOADED", list });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  useEffect(() => {
    fetchResolvedItems(state.searchQuery, state.page);
  }, [state.statusFilter, state.categoryFilter]);

  const handleSearch = (e: ChangeEvent<HTMLElement>) => {
    const searchQuery = (e.target as HTMLInputElement).value;
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { searchQuery, page: 1 },
    });

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(() => {
      fetchResolvedItems(searchQuery, 1);
    }, 500);

    setDebounceTimeout(timeout);
  };

  const onPageChange = (e: ChangeEvent<unknown>, page: number) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { page },
    });
    fetchResolvedItems(state.searchQuery, page);
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await updateItemStatus(id, { status });
      dispatch({ type: "UPDATE_STATUS", _id: id, status });
      enqueueSnackbar(staticTexts.itemStatusUpdatedSuccessfully, {
        variant: "success",
      });
      await fetchResolvedItems(state.searchQuery, state.page);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  const columns = [
    {
      id: "id",
      label: staticTexts.sNo,
    },
    {
      id: "name",
      label: staticTexts.itemName,
    },
    {
      id: "image",
      label: staticTexts.image,
    },
    {
      id: "category",
      label: staticTexts.category,
    },
    {
      id: "location",
      label: staticTexts.location,
    },
    {
      id: "date",
      label: staticTexts.date,
    },
    {
      id: "claimCount",
      label: staticTexts.claimCount,
      align: "center",
    },
  ];

  const createRow = (
    item: any,
    index: number,
    onEdit: any,
    onResolve: any,
    page: number
  ) => {
    const name = (
      <Typography
        className="cursor-pointer"
        color="primary"
        variant="body1"
        onClick={() => onEdit("view", item)}
        sx={{ textTransform: "capitalize" }}
      >
        {item.name}
      </Typography>
    );

    const image =
      item.images?.length > 0 ? (
        <Avatar src={item.images[0]} alt="item" />
      ) : (
        <Avatar sx={{ backgroundColor: "#FFE0B5" }}>
          <CameraAltIcon sx={{ color: "#4A403A" }} />
        </Avatar>
      );

    const status = (
      <Switch
        checked={item.status === "ACTIVE"}
        onChange={(e) => {
          handleStatusChange(
            item._id,
            e.target.checked ? "ACTIVE" : "INACTIVE"
          );
        }}
      />
    );

    const action = (
      <>
        <Tooltip title="Resolve">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            sx={{
              minWidth: "auto",
              padding: "4px 8px",
              textTransform: "capitalize",
            }}
            onClick={() => {
              onResolve(item._id);
            }}
          >
            {staticTexts.resolve}
          </Button>
        </Tooltip>
      </>
    );

    return {
      id: (page - 1) * itemsPerPage + index + 1,
      name,
      image,
      category: item._category?.name?.[language]
        ?.split(" ")
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" "),
      location: handleCharacterLimit(item.location?.name || "", 20),
      date: formatDate(item.eventDate),
      claimCount: (
        <Typography align="center">{item.claimCount || 0}</Typography>
      ),
      status,
      action,
    };
  };

  const fetchChatDetails = async (payload: object) => {
    try {
      const list = await getChatListing(payload);

      if (!list || !list.length) {
        enqueueSnackbar(staticTexts.noChatsFound, {
          variant: "info",
        });
        return;
      }

      dispatch({
        type: "UPDATE_FIELDS",
        payload: { userList: list, loading: false },
      });
      setShowChatListing(true);
    } catch (error: any) {
      enqueueSnackbar(
        error.response.data.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  return (
    <>
      {state.loading && <Loader />}
      <Header
        searchPlaceholder={staticTexts.searchByItemName}
        onSearch={handleSearch}
        searchInput
      >
        {categories && categories.length > 0 && (
          <FormControl className="ml-2" size="small">
            <Select
              size="small"
              displayEmpty
              value={state.categoryFilter}
              renderValue={(selected) => {
                if (!selected || selected === "") {
                  return staticTexts.categoryFilter;
                }
                if (selected === "all") {
                  return staticTexts.all;
                }
                const selectedCategory = categories?.find(
                  (cat: any) => cat._id === selected
                );
                return selectedCategory?.name?.[language]
                  ?.split(" ")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  )
                  .join(" ");
              }}
              onChange={(e) =>
                dispatch({
                  type: "UPDATE_FIELDS",
                  payload: { categoryFilter: e.target.value, page: 1 },
                })
              }
            >
              {(categories || []).map((category: any) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name?.[language]
                    ?.split(" ")
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(" ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Header>

      {/* Show Data  */}
      <Box marginTop="10px">
        <CustomTable
          columns={columns}
          rows={state.list}
          height="calc(100vh - 193px)"
          errorMessage={staticTexts.itemsNotAvailable}
          pagination={{
            page: state.page,
            totalPages: state.totalPages,
          }}
          onPageChange={onPageChange}
        />
      </Box>

      {/* Manage Data  */}
      <Manage
        open={state.manage.isOpen}
        onClose={() => handleManage("view", null)}
        title={staticTexts.foundItemDetails}
        maxWidth="lg"
        titlePadding="16px 24px"
        contentPadding="0px 24px"
        fullWidth={true}
        content={
          <Box>
            {/* Item Details Section */}
            <Typography
              variant="h6"
              fontSize="16px"
              color="#4A403A"
              gutterBottom
            >
              {staticTexts.itemDetails}
            </Typography>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.itemName}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?.name}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.description}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?.description || "N/A"}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.location}
                  </Typography>
                  <Typography
                    component="p"
                    fontSize="14px"
                    color="#4A403A"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {state.manage.item?.location?.name}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.category}
                  </Typography>
                  <Typography
                    component="p"
                    fontSize="14px"
                    color="#4A403A"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {state.manage.item?._category?.name?.[language]}
                  </Typography>
                </Box>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.name}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?._user?.name}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.emailAddress}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?._user?.email || "N/A"}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.phoneNumber}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {`+${state.manage.item?._user?.phone?.prefix} ${state.manage.item?._user?.phone?.number}` ||
                      "N/A"}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {state.manage.item?.type === "FOUND"
                      ? staticTexts.foundDate
                      : staticTexts.lostDate}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {formatDate(state.manage.item?.eventDate)}
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>

            <Typography
              variant="h6"
              fontSize="16px"
              color="#4A403A"
              gutterBottom
            >
              {staticTexts.questionDetails}
            </Typography>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {state.manage.item?.questions?.map((question: any) => (
                  <Box key={question._question?._id} marginBottom="24px">
                    <Typography component="p" fontSize="12px" color="#776E65">
                      {language === "en"
                        ? question._question?.label?.en
                        : question._question?.label?.ar}
                    </Typography>
                    <Typography component="p" fontSize="14px" color="#4A403A">
                      {question?.value || "N/A"}
                    </Typography>
                  </Box>
                ))}
              </Grid2>
            </Grid2>

            {/* Uploaded Images Section */}
            {state.manage.item?.images &&
              state.manage.item.images.length > 0 && (
                <Box mt={4}>
                  <Typography
                    variant="h6"
                    fontSize="16px"
                    color="#4A403A"
                    gutterBottom
                  >
                    {staticTexts.uploadedImages}
                  </Typography>
                  <Box display="flex" gap="8px">
                    {state.manage.item?.images?.map(
                      (imageUrl: string, index: number) => (
                        <img
                          key={index}
                          src={imageUrl}
                          alt={`Item ${index + 1}`}
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "8px",
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>
              )}
            <Box
              display="flex"
              gap="16px"
              justifyContent="end"
              width="100%"
              paddingBottom="24px"
            >
              <Button
                sx={{
                  alignSelf: "flex-end",
                }}
                onClick={() => {
                  fetchChatDetails({
                    itemId: state.manage.item._id,
                    claimToUserId: state.claimToUserId,
                  });
                  handleManage("view", null);
                }}
              >
                {staticTexts.seeChats}
              </Button>
            </Box>
          </Box>
        }
      />

      <WarningDialog
        isOpen={state.resolveWarning}
        onClose={() => handleWarningDialog(state._item || "")}
        onConfirm={() => handleResolve(state._item)}
        title={staticTexts.resolveItem}
        description={staticTexts.areYouSureDoYouWantToResolveThisItem}
      />

      <ChatDetails
        itemId={state.manage.item?._id}
        claimToUserId={state.claimToUserId}
        userList={state.userList}
        showChatListing={showChatListing}
        handleClose={handleClose}
      />
    </>
  );
};

export default withPermission(ResolvedItems, PERMISSIONS.VIEW_RESOLVED_ITEMS);
